import React, { useEffect, useMemo } from 'react';
import { Button, Input, Checkbox } from '../../../ui';
import { useBuilderEditor } from '../../../../features/builder/BuilderEditorContext';
import { QuestionType } from '../../../../core/constants/common';

const ActionsTab = () => {
  const {
    actionsarray, getActionDetails, fieldeditid, isActionNeeded, selectedanswertype,
    choiceoptions, gridData, actionTaken, messageUpdate, emailUpdate, chooseActionOption, addAction,
  } = useBuilderEditor();

  useEffect(() => {
    if (fieldeditid) getActionDetails();
  }, [fieldeditid, getActionDetails]);

  const actionOptions = useMemo(() => {
    if (selectedanswertype === QuestionType.RADIO_GRID) {
      return gridData.rowSets.flatMap((rowSet) =>
        (rowSet.rows || []).flatMap((cell, colIndex) => {
          if (!cell.id) return [];
          const columnName = gridData.columns[colIndex]?.name || `Column ${colIndex + 1}`;
          const rowName = rowSet.name || 'Row';
          const label = cell.option || cell.name || columnName;
          return [{ id: cell.id, name: `${rowName} — ${columnName}: ${label}` }];
        })
      );
    }
    return choiceoptions.filter((o) => !o.showOthers);
  }, [selectedanswertype, gridData, choiceoptions]);

  if (!isActionNeeded()) {
    return <p className="builder-text-secondary">Actions are not available for this question type.</p>;
  }

  if (!fieldeditid) {
    return <p className="builder-text-secondary">Save the question first to configure actions.</p>;
  }

  return (
    <div className="builder-property-tab">
      {actionsarray.map((action, index) => (
        <div key={index} className="builder-property-tab__card">
          <label className="builder-label">If the answer is</label>
          <select
            className="builder-ui-select builder-property-tab__select"
            value={action.formOption?.id || ''}
            onChange={(e) => chooseActionOption(index, e.target.value)}
          >
            <option value="">Select</option>
            {actionOptions.map((opt) => (
              <option key={opt.id || opt.name} value={opt.id}>{opt.name}</option>
            ))}
          </select>

          <p className="builder-label builder-property-tab__label--compact">Actions to be taken</p>
          <div className="builder-property-tab__actions-container">
            <Checkbox
              label="Send email to"
              checked={action.types?.includes('EMAIL')}
              onChange={(e) => actionTaken(e.target.checked, index, 'EMAIL')}
            />
            {action.types?.includes('EMAIL') && (
              <div className="builder-property-tab__nested-fields">
                <Input
                  placeholder='Email addresses separated by ","'
                  value={Array.isArray(action.emailIds) ? action.emailIds.join(',') : ''}
                  onChange={(e) => emailUpdate(index, 'emailIds', e.target.value)}
                />
                <textarea
                  className="builder-ui-textarea builder-property-tab__spacer-top"
                  rows={3}
                  placeholder="Email message"
                  value={action.emailMessage || ''}
                  onChange={(e) => messageUpdate(index, 'emailMessage', e.target.value)}
                />
              </div>
            )}

            <Checkbox
              label="Send notification in the system"
              checked={action.types?.includes('NOTIFICATION')}
              onChange={(e) => actionTaken(e.target.checked, index, 'NOTIFICATION')}
            />
            {action.types?.includes('NOTIFICATION') && (
              <textarea
                className="builder-ui-textarea builder-property-tab__nested-textarea"
                rows={3}
                value={action.notification || ''}
                onChange={(e) => messageUpdate(index, 'notification', e.target.value)}
              />
            )}

            <Checkbox
              label="Show alert in the dashboard"
              checked={action.types?.includes('TASK')}
              onChange={(e) => actionTaken(e.target.checked, index, 'TASK')}
            />
            {action.types?.includes('TASK') && (
              <textarea
                className="builder-ui-textarea builder-property-tab__nested-textarea--last"
                rows={3}
                value={action.task || ''}
                onChange={(e) => messageUpdate(index, 'task', e.target.value)}
              />
            )}
          </div>
        </div>
      ))}

      <Button variant="secondary" onClick={addAction}>Add another action</Button>
    </div>
  );
};

export default ActionsTab;
